//
// Copyright Inrupt Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to use,
// copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
// Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
// INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
// PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
// HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
// OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
// SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
//

import type { Url, UrlString } from "../../interfaces";
import type { WithAccessibleAcr } from "../acp";
import { ACP } from "../constants";
import { getDefaultAccessControlThing } from "../internal/getDefaultAccessControlThing";
import { DEFAULT_MEMBER_ACR_ACCESS_CONTROL } from "../internal/getDefaultAccessControlUrl";
import { setAccessControlResourceThing } from "../internal/setAccessControlResourceThing";
import { removeIri } from "../../thing/remove";

/**
 * ```{note}
 * The ACP specification is a draft. As such, this function is experimental and
 * subject to change, even in a non-major release.
 * See also: https://solid.github.io/authorization-panel/acp-specification/
 * ```
 *
 * Remove a policy applying to the ACRs of the given resource's children.
 *
 * @param resourceWithAcr The resource for which to remove the URL of a policy
 * applying to its children's access control resources.
 * @param policyUrl A Policy URL.
 * @returns The resource with its ammended access control resource.
 * @since 1.16.1
 */
export function removeMemberAcrPolicyUrl<T extends WithAccessibleAcr>(
  resourceWithAcr: T,
  policyUrl: Url | UrlString,
): T {
  const defaultAccessControlThing = getDefaultAccessControlThing(
    resourceWithAcr,
    DEFAULT_MEMBER_ACR_ACCESS_CONTROL,
  );

  return setAccessControlResourceThing(
    resourceWithAcr,
    removeIri(defaultAccessControlThing, ACP.access, policyUrl),
  );
}
